import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight, CheckCircle, X, Lightbulb,
  BookOpen, Loader2, ChevronDown, Star, Target,
} from "lucide-react";
import gkApi from "../api/gk";
import { GkHeader } from "./GkLibrary";
import SEOHead from "../components/SEOHead";
import { useAuth } from "../hooks/useAuth";
import StudyBar from "../components/StudyBar";

const DIFF_STYLE = {
  easy:   { bg: "#ECFDF5", color: "#059669", label: "Easy" },
  medium: { bg: "#FFFBEB", color: "#D97706", label: "Medium" },
  hard:   { bg: "#FEF2F2", color: "#DC2626", label: "Hard"   },
};

/* ── Inline markdown: bold, italic, inline-code ─────────────────────────── */
function Inline({ text }) {
  // split on **bold**, *italic*, `code`
  const tokens = text.split(/(\*\*.*?\*\*|\*.*?\*|`.*?`)/g);
  return (
    <>
      {tokens.map((tok, i) => {
        if (tok.startsWith('**') && tok.endsWith('**'))
          return <strong key={i} className="font-bold text-gray-900">{tok.slice(2, -2)}</strong>;
        if (tok.startsWith('*') && tok.endsWith('*'))
          return <em key={i}>{tok.slice(1, -1)}</em>;
        if (tok.startsWith('`') && tok.endsWith('`'))
          return <code key={i} className="bg-gray-100 text-rose-600 px-1.5 py-0.5 rounded text-[12px] font-mono">{tok.slice(1, -1)}</code>;
        return tok;
      })}
    </>
  );
}

/* ── Full markdown renderer ──────────────────────────────────────────────── */
function Prose({ text }) {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // --- horizontal rule
    if (/^---+$/.test(line.trim())) {
      elements.push(<hr key={i} className="border-gray-200 my-4" />);
      i++; continue;
    }

    // # Headings
    const h1 = line.match(/^# (.+)/);
    if (h1) {
      elements.push(
        <h2 key={i} className="text-base font-sora font-black text-gray-900 mt-6 mb-2 pb-1 border-b border-gray-200">
          <Inline text={h1[1]} />
        </h2>
      );
      i++; continue;
    }
    const h2 = line.match(/^## (.+)/);
    if (h2) {
      elements.push(
        <h3 key={i} className="text-[13px] font-sora font-extrabold text-gray-800 mt-5 mb-1.5">
          <Inline text={h2[1]} />
        </h3>
      );
      i++; continue;
    }
    const h3 = line.match(/^### (.+)/);
    if (h3) {
      elements.push(
        <h4 key={i} className="text-[12px] font-sora font-bold text-gray-700 mt-4 mb-1 uppercase tracking-wide">
          <Inline text={h3[1]} />
        </h4>
      );
      i++; continue;
    }

    // > blockquote
    if (line.startsWith('> ')) {
      elements.push(
        <blockquote key={i} className="border-l-4 border-yellow-400 bg-yellow-50 px-4 py-2 rounded-r-lg my-2 text-[13px] text-gray-700 italic">
          <Inline text={line.slice(2)} />
        </blockquote>
      );
      i++; continue;
    }

    // Table: collect all | rows
    if (line.trim().startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      // filter out separator rows (|---|---|)
      const dataRows = tableLines.filter(r => !/^\|[\s\-|]+\|$/.test(r.trim()));
      const [header, ...bodyRows] = dataRows;
      const parseCells = r => r.split('|').map(c => c.trim()).filter(Boolean);

      elements.push(
        <div key={`table-${i}`} className="overflow-x-auto my-3 rounded-xl border border-gray-200">
          <table className="w-full text-[12px]">
            {header && (
              <thead className="bg-gray-50">
                <tr>
                  {parseCells(header).map((c, ci) => (
                    <th key={ci} className="px-3 py-2 text-left font-bold text-gray-700 border-b border-gray-200 whitespace-nowrap">
                      <Inline text={c} />
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {bodyRows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {parseCells(row).map((c, ci) => (
                    <td key={ci} className="px-3 py-2 text-gray-700 border-b border-gray-100">
                      <Inline text={c} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Ordered list: collect consecutive "N. ..." lines
    if (/^\d+\. /.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ''));
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`} className="list-decimal list-inside space-y-1 my-2 text-[13px] text-gray-700 pl-2">
          {items.map((item, ii) => (
            <li key={ii}><Inline text={item} /></li>
          ))}
        </ol>
      );
      continue;
    }

    // Unordered list: "- " or "* "
    if (/^[-*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        items.push(lines[i].replace(/^[-*] /, ''));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="list-disc list-inside space-y-1 my-2 text-[13px] text-gray-700 pl-2">
          {items.map((item, ii) => (
            <li key={ii}><Inline text={item} /></li>
          ))}
        </ul>
      );
      continue;
    }

    // blank line
    if (line.trim() === '') { i++; continue; }

    // Normal paragraph
    elements.push(
      <p key={i} className="text-[13px] text-gray-700 leading-relaxed my-1">
        <Inline text={line} />
      </p>
    );
    i++;
  }

  return <div className="space-y-0.5">{elements}</div>;
}

/* ── Interactive MCQ ─────────────────────────────────────────────────────── */
function McqSection({ mcqs, navigate }) {
  const [qi, setQi]         = useState(0);
  const [sel, setSel]       = useState(null);
  const [showExp, setShowExp] = useState(false);
  const [score, setScore]   = useState({ c: 0, a: 0 });

  if (!mcqs?.length) return null;

  const Q = mcqs[qi];
  const done = sel !== null;
  const finished = score.a === mcqs.length && qi === mcqs.length - 1 && done;

  function answer(i) {
    if (sel !== null) return;
    setSel(i); setShowExp(true);
    setScore(s => ({ c: s.c + (i === Q.ans ? 1 : 0), a: s.a + 1 }));
  }
  function next() {
    if (qi < mcqs.length - 1) { setQi(q => q + 1); setSel(null); setShowExp(false); }
  }
  function reset() { setQi(0); setSel(null); setShowExp(false); setScore({ c: 0, a: 0 }); }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between"
        style={{ background: "linear-gradient(90deg,#1A2810,#243018)" }}>
        <p className="text-xs font-extrabold uppercase tracking-wide" style={{ color: "#C9A227" }}>
          Practice MCQs
        </p>
        <span className="text-xs font-semibold text-white">{qi + 1} / {mcqs.length}</span>
      </div>

      <div className="p-5">
        {/* Progress bar */}
        <div className="h-1 bg-gray-100 rounded-full mb-4">
          <div className="h-1 rounded-full transition-all" style={{ width: `${((qi + 1) / mcqs.length) * 100}%`, background: "#C9A227" }} />
        </div>

        {/* Score */}
        {score.a > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold text-green-600">{score.c} correct</span>
            <span className="text-gray-300">·</span>
            <span className="text-xs text-gray-400">{score.a - score.c} wrong</span>
            <span className="text-gray-300">·</span>
            <span className="text-xs text-gray-400">{score.a} attempted</span>
          </div>
        )}

        <p className="font-sora font-semibold text-sm text-gray-900 mb-4 leading-snug">{Q.q}</p>

        <div className="space-y-2 mb-4">
          {Q.opts.map((opt, i) => {
            let style = "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50";
            if (done) {
              if (i === Q.ans) style = "border-green-400 bg-green-50 text-green-800";
              else if (i === sel) style = "border-red-400 bg-red-50 text-red-700";
              else style = "border-gray-100 bg-gray-50 text-gray-400";
            }
            return (
              <button key={i} onClick={() => answer(i)} disabled={done}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border text-sm font-medium text-left transition-all ${style}`}>
                <span className="w-6 h-6 rounded-full border border-current flex-shrink-0 flex items-center justify-center text-[11px] font-extrabold">
                  {String.fromCharCode(65 + i)}
                </span>
                {opt}
                {done && i === Q.ans && <CheckCircle size={14} className="ml-auto text-green-500 flex-shrink-0" />}
                {done && i === sel && i !== Q.ans && <X size={14} className="ml-auto text-red-500 flex-shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Explanation */}
        <AnimatePresence>
          {showExp && Q.exp && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl p-3 mb-4">
                <Lightbulb size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800 leading-relaxed">{Q.exp}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex gap-2">
          {done && qi < mcqs.length - 1 && (
            <button onClick={next}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
              style={{ background: "#1A2810" }}>
              Next Question →
            </button>
          )}
          {(finished || (done && qi === mcqs.length - 1)) && (
            <button onClick={reset}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all border border-gray-200 text-gray-700 hover:bg-gray-50">
              Retry All
            </button>
          )}
        </div>
      </div>

      {/* Free signup nudge — GK is free, upsell mock tests */}
      <div className="border-t border-gray-100 bg-gray-50 px-5 py-3 flex items-center justify-between gap-3">
        <p className="text-xs text-gray-500">
          GK is <strong className="text-green-600">100% free</strong> — sign up to save progress &amp; attempt full mock tests
        </p>
        <button onClick={() => navigate("/login")}
          className="flex-shrink-0 text-xs font-extrabold px-3 py-1.5 rounded-lg whitespace-nowrap"
          style={{ background: "#C9A227", color: "#1A2810" }}>
          Join Free →
        </button>
      </div>
    </div>
  );
}

/* ── Main GkTopic page ───────────────────────────────────────────────────── */
export default function GkTopic() {
  const { category, slug } = useParams();
  const navigate           = useNavigate();
  const { user }           = useAuth();

  const [topic, setTopic]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [expandFacts, setExpandFacts] = useState(false);

  useEffect(() => {
    setLoading(true);
    gkApi.getBySlug(category, slug)
      .then(setTopic)
      .catch(() => setError("Topic not found or unavailable."))
      .finally(() => setLoading(false));
  }, [category, slug]);

  const PRACTICE_IDS = { "english": "English", "mathematics": "Mathematics", "ssb-preparation": "SSB Prep" };
  const isPractice   = !!PRACTICE_IDS[category];
  const backLabel    = isPractice ? PRACTICE_IDS[category] : "GK Library";
  const backPath     = "/gk";

  if (loading) return (
    <div className="min-h-screen bg-[#F5F6FA] font-body">
      <GkHeader category={category} />
      <div className="flex justify-center py-24"><Loader2 size={28} className="animate-spin text-gray-300" /></div>
    </div>
  );

  if (error || !topic) return (
    <div className="min-h-screen bg-[#F5F6FA] font-body">
      <GkHeader category={category} />
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <p className="text-gray-500 text-sm mb-4">{error || "Topic not found."}</p>
        <button onClick={() => navigate(`/gk/${category}`)} className="text-sm font-bold text-yellow-600 underline">← Back to {backLabel}</button>
      </div>
    </div>
  );

  const diff  = DIFF_STYLE[topic.difficulty] || DIFF_STYLE.medium;
  const examTip = topic.facts?.filter(f => f.examTip);
  const allFacts = topic.facts || [];
  const visibleFacts = expandFacts ? allFacts : allFacts.slice(0, 6);

  return (
    <div className="min-h-screen bg-[#F5F6FA] font-body">
      {topic && (
        <SEOHead
          title={`${topic.title} — ${topic.categoryLabel || category} GK | EduPulseAI`}
          description={topic.facts?.[0]?.fact ? topic.facts[0].fact.slice(0, 150) + '...' : `Study ${topic.title} for NDA, CDS and CAPF exam preparation. Key facts, exam tips and MCQ practice.`}
          keywords={`${topic.title}, ${category} for NDA, ${category} GK notes, ${topic.title} UPSC`}
          url={`/gk/${category}/${slug}`}
          jsonLd={{ "@context": "https://schema.org", "@type": "Article", "headline": topic.title, "description": topic.facts?.[0]?.fact || topic.title, "publisher": { "@type": "Organization", "name": "EduPulseAI" } }}
        />
      )}
      <GkHeader category={category} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-1.5 text-xs text-gray-400 flex-wrap">
          {!isPractice && (
            <>
              <button onClick={() => navigate(backPath)} className="hover:text-gray-700 transition-colors">{backLabel}</button>
              <ChevronRight size={11} />
            </>
          )}
          <button onClick={() => navigate(`/gk/${category}`)} className="hover:text-gray-700 transition-colors capitalize">
            {topic.categoryLabel}
          </button>
          <ChevronRight size={11} />
          <span className="text-gray-700 font-semibold line-clamp-1">{topic.title}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-28 sm:pb-8">
        <div className="grid lg:grid-cols-[1fr_300px] gap-6">

          {/* ── LEFT: main content ── */}
          <div className="space-y-5">

            {/* Title card */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="h-2" style={{ background: "linear-gradient(90deg,#1A2810,#C9A227)" }} />
              <div className="p-6">
                {/* Meta */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="text-xs font-bold px-2 py-1 rounded-lg"
                    style={{ background: diff.bg, color: diff.color }}>
                    {diff.label}
                  </span>
                  {(topic.examTags || []).map(t => (
                    <span key={t} className="text-xs font-bold bg-gray-100 text-gray-600 px-2 py-1 rounded-lg">{t}</span>
                  ))}
                  {topic.source && (
                    <span className="text-xs text-gray-400 ml-auto font-medium">Source: {topic.source}</span>
                  )}
                </div>

                <h1 className="font-sora font-black text-xl md:text-2xl text-gray-900 mb-3">{topic.title}</h1>

                {topic.summary && (
                  <p className="text-sm text-gray-600 leading-relaxed border-l-4 pl-4 py-1" style={{ borderColor: "#C9A227" }}>
                    {topic.summary}
                  </p>
                )}
              </div>
            </div>

            {/* Key Facts */}
            {allFacts.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <p className="font-sora font-bold text-sm text-gray-900">Key Facts</p>
                  <span className="text-xs text-gray-400">{allFacts.length} facts</span>
                </div>
                <div className="p-5">
                  <div className="space-y-2.5">
                    {visibleFacts.map((f, i) => (
                      <motion.div key={i}
                        initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04 }}
                        className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {f.examTip
                            ? <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "#FFFBEB" }}>
                                <Star size={11} className="fill-yellow-400 text-yellow-400" />
                              </span>
                            : <CheckCircle size={15} className="text-green-500" />
                          }
                        </div>
                        <p className={`text-sm leading-relaxed ${f.examTip ? "text-gray-900 font-medium" : "text-gray-600"}`}>
                          {f.fact}
                          {f.examTip && (
                            <span className="ml-2 text-[10px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded"
                              style={{ background: "#FEF3C7", color: "#92400E" }}>exam tip</span>
                          )}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                  {allFacts.length > 6 && (
                    <button onClick={() => setExpandFacts(v => !v)}
                      className="mt-4 flex items-center gap-1 text-xs font-bold transition-colors"
                      style={{ color: "#C9A227" }}>
                      {expandFacts ? "Show less" : `Show ${allFacts.length - 6} more facts`}
                      <ChevronDown size={13} className={`transition-transform ${expandFacts ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Full content */}
            {topic.content && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                <p className="font-sora font-bold text-sm text-gray-900 mb-4 flex items-center gap-2">
                  <BookOpen size={15} /> Detailed Notes
                </p>
                <Prose text={topic.content} />
              </div>
            )}

            {/* MCQ section */}
            <McqSection mcqs={topic.mcqs} navigate={navigate} />

            {/* Related topics */}
            {topic.relatedTopics?.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                <p className="font-sora font-bold text-sm text-gray-900 mb-3">Related Topics</p>
                <div className="space-y-2">
                  {topic.relatedTopics.map(r => (
                    <button key={r.slug} onClick={() => navigate(`/gk/${r.category}/${r.slug}`)}
                      className="w-full flex items-center justify-between gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all text-left group">
                      <div>
                        <p className="font-semibold text-sm text-gray-800 group-hover:text-gray-900">{r.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5 capitalize">{r.categoryLabel}</p>
                      </div>
                      <ChevronRight size={14} className="text-gray-400 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT: sidebar ── */}
          <div className="space-y-4">

            {/* Signup card */}
            <div className="rounded-2xl overflow-hidden shadow-lg" style={{ background: "linear-gradient(135deg,#1A2810,#243018)" }}>
              <div className="px-4 py-2" style={{ background: "#C9A227" }}>
                <p className="text-xs font-extrabold uppercase tracking-wide" style={{ color: "#1A2810" }}>
                  <Target size={11} className="inline mr-1" /> Free · No Credit Card
                </p>
              </div>
              <div className="p-4">
                <p className="text-sm text-white font-semibold mb-3">What's free vs paid</p>
                {[
                  { label: "Full GK Library", free: true },
                  { label: "Daily CA digest 6 AM", free: true },
                  { label: "5 mock tests / month", free: true },
                  { label: "Specialised exam notes", free: false },
                  { label: "Unlimited mock tests + AIR", free: false },
                  { label: "PYQ papers + SSB prep", free: false },
                ].map(f => (
                  <div key={f.label} className="flex items-center gap-2 text-xs mb-1.5">
                    {f.free
                      ? <CheckCircle size={11} className="text-green-400 flex-shrink-0" />
                      : <span className="text-[10px] font-extrabold px-1 rounded flex-shrink-0" style={{ background: "#C9A227", color: "#1A2810" }}>₹149</span>
                    }
                    <span style={{ color: f.free ? "#A8B89A" : "#6B7280" }}>{f.label}</span>
                  </div>
                ))}
                <button onClick={() => navigate("/login")}
                  className="w-full mt-4 py-2.5 rounded-xl text-sm font-extrabold transition-all"
                  style={{ background: "#C9A227", color: "#1A2810" }}>
                  Create Free Account →
                </button>
                <button onClick={() => navigate("/login")}
                  className="w-full mt-2 py-2 rounded-xl text-xs font-semibold text-white transition-all"
                  style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" }}>
                  See Paid Plans →
                </button>
              </div>
            </div>

            {/* Exam tip summary */}
            {examTip?.length > 0 && (
              <div className="bg-white border border-yellow-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-yellow-50 px-4 py-2.5 border-b border-yellow-100 flex items-center gap-2">
                  <Star size={13} className="fill-yellow-400 text-yellow-400" />
                  <p className="text-xs font-extrabold text-yellow-800 uppercase tracking-wide">Must-Know for Exam</p>
                </div>
                <div className="p-4 space-y-2">
                  {examTip.map((f, i) => (
                    <p key={i} className="text-xs text-gray-700 leading-relaxed pl-3 border-l-2 border-yellow-400">{f.fact}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Topic metadata */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm text-xs text-gray-500 space-y-2">
              <p className="font-sora font-bold text-sm text-gray-800 mb-3">Topic Info</p>
              <div className="flex justify-between"><span>Category</span><span className="font-semibold text-gray-700 capitalize">{topic.categoryLabel}</span></div>
              {topic.subcategoryLabel && <div className="flex justify-between"><span>Subtopic</span><span className="font-semibold text-gray-700">{topic.subcategoryLabel}</span></div>}
              <div className="flex justify-between"><span>Difficulty</span><span className="font-semibold" style={{ color: diff.color }}>{diff.label}</span></div>
              <div className="flex justify-between"><span>Source</span><span className="font-semibold text-gray-700">{topic.source || "—"}</span></div>
              <div className="flex justify-between"><span>Facts</span><span className="font-semibold text-gray-700">{allFacts.length}</span></div>
              <div className="flex justify-between"><span>MCQs</span><span className="font-semibold text-gray-700">{topic.mcqs?.length || 0}</span></div>
              <div className="flex justify-between"><span>Views</span><span className="font-semibold text-gray-700">{topic.viewCount?.toLocaleString() || 0}</span></div>
              {topic.verified && (
                <div className="flex items-center gap-1.5 pt-1 border-t border-gray-100 text-green-600 font-semibold">
                  <CheckCircle size={12} />Verified by educators
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <StudyBar
        itemType="gk"
        itemSlug={`${category}/${slug}`}
        itemTitle={topic.title}
        isLoggedIn={!!user}
      />
    </div>
  );
}
